import React, { useCallback, useEffect, useState } from 'react';
import { pendingOffersApi } from '../../api/pendingOffers';
import { bindNoteRefresh } from '../../utils/noteChanged';
import { offerKindLabel } from '../../utils/chatIdeaOffers';
import {
  ACCEPT_NOTE_UPDATE,
  ADD_TO_BOOK_NOTES,
  CAST_NOT_NOW,
  DOCS,
  IDEA_OFFER_DISMISS_FAILED,
  IDEA_OFFER_SAVE_FAILED,
  PENDING_OFFERS,
  REDRAFT_ACCEPT,
  REDRAFT_ACCEPTED,
  REDRAFT_NOTE_CHANGED,
  REDRAFT_OFFER_HEADING,
  REDRAFT_OFFER_LINE,
  REDRAFT_REJECT,
  REDRAFT_REJECTED,
  SKIP_NOTE_UPDATE,
} from '../../utils/writerCopy';

function labelFor(offer) {
  if (offer.kind === 'chat') return offerKindLabel(offer.entity_type || offer.offer_kind);
  if (offer.kind === 'docs') return DOCS;
  return '';
}

function acceptLabel(kind) {
  if (kind === 'chat') return ADD_TO_BOOK_NOTES;
  if (kind === 'redraft') return REDRAFT_ACCEPT;
  return ACCEPT_NOTE_UPDATE;
}

function rejectLabel(kind) {
  if (kind === 'chat') return CAST_NOT_NOW;
  if (kind === 'redraft') return REDRAFT_REJECT;
  return SKIP_NOTE_UPDATE;
}

/**
 * Every offered idea that is not saved yet: chat cards, Scene Accept
 * notes, and Docs offers. Accept and Not now / Skip call the same
 * actions chat uses.
 */
export default function PendingOffers({ storyId, onChanged }) {
  const [offers, setOffers] = useState([]);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!storyId) {
      setOffers([]);
      return;
    }
    try {
      const data = await pendingOffersApi.list(storyId);
      setOffers(Array.isArray(data?.offers) ? data.offers : []);
      setError('');
    } catch {
      setOffers([]);
    }
  }, [storyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!storyId) return undefined;
    return bindNoteRefresh(storyId, () => { void load(); });
  }, [storyId, load]);

  if (!storyId || (offers.length === 0 && !notice)) return null;

  const run = async (offer, action) => {
    const id = String(offer.id);
    setBusyId(id);
    setError('');
    try {
      const body = { kind: offer.kind, id: offer.id, source_ref: offer.source_ref || null };
      if (action === 'accept') await pendingOffersApi.accept(storyId, body);
      else await pendingOffersApi.reject(storyId, body);
      if (offer.kind === 'redraft') {
        setNotice(action === 'accept' ? REDRAFT_ACCEPTED : REDRAFT_REJECTED);
      }
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      const stale = offer.kind === 'redraft' && err?.status === 409;
      setError(stale
        ? REDRAFT_NOTE_CHANGED
        : (action === 'accept' ? IDEA_OFFER_SAVE_FAILED : IDEA_OFFER_DISMISS_FAILED));
    } finally {
      setBusyId('');
    }
  };

  return (
    <section className="bs-pending" aria-label={PENDING_OFFERS} data-testid="pending-offers">
      <h2 className="bs-pending__title">{PENDING_OFFERS}</h2>
      <ul className="bs-pending__list">
        {offers.map((offer) => {
          const busy = busyId === String(offer.id);
          const label = labelFor(offer);
          return (
            <li key={`${offer.kind}-${offer.id}`} className="bs-pending__item" data-testid="pending-offer">
              {offer.kind === 'redraft' ? (
                <>
                  <h3 className="bs-pending__name">{REDRAFT_OFFER_HEADING}</h3>
                  <span className="bs-pending__detail">{REDRAFT_OFFER_LINE}</span>
                </>
              ) : (
                <span className="bs-pending__name">{offer.title}</span>
              )}
              {label ? <span className="bs-pending__label">{label}</span> : null}
              {offer.detail && offer.detail !== offer.title ? (
                <span className="bs-pending__detail">{offer.detail}</span>
              ) : null}
              <span className="bs-pending__actions">
                <button
                  type="button"
                  className={
                    offer.kind === 'chat' ? 'bs-btn bs-btn--offer'
                    : offer.kind === 'redraft' ? 'bs-btn bs-btn--secondary'
                    : 'bs-btn bs-btn--primary'
                  }
                  disabled={busy}
                  onClick={() => { void run(offer, 'accept'); }}
                >
                  {acceptLabel(offer.kind)}
                </button>
                <button
                  type="button"
                  className="bs-btn bs-btn--secondary"
                  disabled={busy}
                  onClick={() => { void run(offer, 'reject'); }}
                >
                  {rejectLabel(offer.kind)}
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      {notice ? <p className="bs-saved-on" role="status">{notice}</p> : null}
      {error ? <p className="bs-pending__error" role="alert">{error}</p> : null}
    </section>
  );
}
